import { SetStateAction, useEffect, useState } from "react";
import MessageInfoTableList, { GridCol, GridRow } from "../../components/table/MessageInfoTableList";
import { auth } from "../../firebase";
import { ChevronDownIcon, ChevronLeftIcon, ChevronUpIcon, ListBulletIcon, MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import { useNavigate, useParams } from "react-router-dom";
import messagesQuery from "../../firestore/Messages.query";
import { useDispatch } from "react-redux";
import { alertOpen, AlertValue } from "../../features/alert.slice";
import userQuery from "../../firestore/User.query";
import Button from "../../components/button/Button";
import { TextField } from "../../components/fields/Fields";
import { SelectFields } from "../../components/select/SelectFields";
import { Messages } from "../../model/Message";
import { convertDateToJapaneseString, convertTimeToString } from "../../utility/ConvertUtily";
import { Spiner } from "../../components/spinner/Spiner";
import { Progress } from "../../components/progress/Progress";
import { ConstMessage } from "../../config/const";
import { QuestionnaireResultsExportModal } from "./QuestionnaireResultsExportModal";
import companiesQuery from "../../firestore/Companies.query";
import { Company } from "../../model/Companies";

type AnswerCounts = {
  answer1: { [key: string]: number };
  answer2: { [key: string]: number };
  answer3: { [key: string]: number };
  answerResult: { [key: string]: number };
  total: number;
};

export const QuestionnaireResults = () => {
  const navigate = useNavigate();
  const [rowList, setRowList] = useState<GridRow[]>([]);
  const [columns, setColumns] = useState<GridCol[]>([]);
  const [questionnaire, setQuestionnaire] = useState<{
    target?: string;
    title1?: string;
    title2?: string;
    title3?: string;
    answers1?: string[];
    answers2?: string[];
    answers3?: string[];
  }>({});
  const { messageId } = useParams<{ messageId: string }>();
  const dispatch = useDispatch();
  const [searchFlg, setSearchFlg] = useState(true);
  const [aggregationFlg, setAggregationFlg] = useState(true);
  const [filterName, setFilterName] = useState("");
  const [filterAnswerResult, setFilterAnswerResult] = useState("");
  const [filterReplyList, setFilterReplyList] = useState("");
  const [filterAnswer1, setFilterAnswer1] = useState("");
  const [filterAnswer2, setFilterAnswer2] = useState("");
  const [filterAnswer3, setFilterAnswer3] = useState("");
  const [answers1List, setAnswers1List] = useState([]);
  const [answers2List, setAnswers2List] = useState([]);
  const [answers3List, setAnswers3List] = useState([]);
  const [originalRowList, setOriginalRowList] = useState<GridRow[]>([]);
  const [message, setMessage] = useState<Messages>();
  const [isReplyListFilterActive, setIsReplyListFilterActive] = useState(false);
  const [isLoad, setIsLoad] = useState(true);
  const [company, setCompany] = useState<Company>();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [loadProgress, setLoadProgress] = useState<number>(-1);

  const replyList = [
    { id: "", name: "" },
    { id: "会場", name: "会場" },
    { id: "オンライン", name: "オンライン" },
  ];

  const answerResultList = [
    { id: "", name: "" },
    { id: "あり", name: "あり" },
    { id: "なし", name: "なし" }
  ];

  const [answerCounts, setAnswerCounts] = useState<AnswerCounts>({
    answer1: {},
    answer2: {},
    answer3: {},
    answerResult: {},
    total: 0,
  });

  const countAnswers = (answers: any[], index: string, options: string[]): { [key: string]: number } => {
    const counts: { [key: string]: number } = {};
    options.forEach(option => {
      counts[option] = 0;
    });
    answers.forEach(answer => {
      if (answer?.rowData?.[index]) {
        counts[answer.rowData[index]] = (counts[answer.rowData[index]] || 0) + 1;
      }
    });
    return counts;
  };

  useEffect(() => {
    const f = async () => {
      try {
        setIsLoad(true);
        const message = await messagesQuery.getMessage(messageId!);
        setMessage(message);
        const results = await messagesQuery.getReadedAndReply(messageId!);
        const questionnaireData = await messagesQuery.getQuestionnaire(messageId!);
        setQuestionnaire(questionnaireData);
        const currentUser = auth.currentUser;
        if (!currentUser || !currentUser.tenantId) {
          throw new Error("Current user is not authenticated or tenantId is missing");
        }
        // 団体を取得
        const company = await companiesQuery.getCompany(currentUser?.tenantId!);
  
        // 質問の回答１
        let answers1List: any = [{ id: "", name: "" }];
        questionnaireData?.answers1!.forEach((answers1: string) => {
          answers1List.push({ id: answers1, name: answers1 });
        });
        setAnswers1List(answers1List);
  
        // 質問の回答２
        let answers2List: any = [{ id: "", name: "" }];
        questionnaireData?.answers2!.forEach((answers2: string) => {
          answers2List.push({ id: answers2, name: answers2 });
        });
        setAnswers2List(answers2List);
  
        // 質問の回答３
        let answers3List: any = [{ id: "", name: "" }];
        questionnaireData?.answers3!.forEach((answers3: string) => {
          answers3List.push({ id: answers3, name: answers3 });
        });
        setAnswers3List(answers3List);
  
        // 出欠結果の初期値設定
        if (message.replyButton === '予定表の出欠(オンライン)' && questionnaireData.target === 'オンライン参加のみ') {
          setFilterReplyList("オンライン");
          setIsReplyListFilterActive(false);
        } else if (message.replyButton === '予定表の出欠' && questionnaireData.target === '全員') {
          setFilterReplyList("会場");
          setIsReplyListFilterActive(false);
        } else {
          setIsReplyListFilterActive(true);
        }
  
        const rows = await Promise.all(
          results.map(async (result) => {
            // アンケート対象者のみ表示
            var dispCheck = false;
            if (message.replyButton === '予定表の出欠' && result.attendanceButtonName === '会場で参加') {
              dispCheck = true;
            } else if (message.replyButton === '予定表の出欠(オンライン)') {
              if (questionnaireData.target === '全員' && (result.attendanceButtonName === '会場で参加' || result.attendanceButtonName === 'オンラインで参加')) {
                dispCheck = true;
              } else if (questionnaireData.target === 'オンライン参加のみ' && result.attendanceButtonName === 'オンラインで参加') {
                dispCheck = true;
              }
            }
            const user = await userQuery.getUser(currentUser?.tenantId!, result.userId);
            // アンケートに回答していない利用不可の利用者は対象外
            if((user.deleteFlg || user.accountSuspensionFlg) && result.answersFlg != true){
              dispCheck = false
            }
            if (dispCheck) {
              const userId = user.id.slice(-11);
              const attendanceDisplay = result.attendanceButtonName === '会場で参加' ? '会場' : 'オンライン';
              const rowData: { [key: string]: any } = {
                id: userId,
                userName: user.lastName,
                attendanceButtonName: attendanceDisplay,
                answerResult: result.answersFlg ? "あり" : "なし",
              };
              if (result.answers) {
                if (questionnaireData.title1) {
                  rowData["answer1"] = result.answers[0];
                }
                if (questionnaireData.title2) {
                  rowData["answer2"] = result.answers[1];
                }
                if (questionnaireData.title3) {
                  rowData["answer3"] = result.answers[2];
                }
              }
              return { rowData };
            }
          })
        );
  
        const filteredRows = rows.filter(row => row !== undefined);
        setRowList(filteredRows);
        setOriginalRowList(filteredRows);

        const answerCounts = {
          answer1: countAnswers(filteredRows, "answer1", answers1List.slice(1).map((a: { name: any; }) => a.name)),
          answer2: countAnswers(filteredRows, "answer2", answers2List.slice(1).map((a: { name: any; }) => a.name)),
          answer3: countAnswers(filteredRows, "answer3", answers3List.slice(1).map((a: { name: any; }) => a.name)),
          answerResult: countAnswers(filteredRows, "answerResult", ["あり", "なし"]),
          total: filteredRows.length,
        };
        
        setAnswerCounts(answerCounts);
  
        const dynamicColumns = [
          { field: "id", headerName: "利用者番号", style: "w-1/6", sort: true },
          {
            field: "userName",
            headerName: "名前",
            style: "w-1/6",
            sort: false,
          },
          {
            field: "attendanceButtonName",
            headerName: "出欠結果",
            style: "w-1/6",
            sort: false,
          },
          {
            field: "answerResult",
            headerName: "回答状況",
            style: "w-1/6",
            sort: false,
          },
        ];
        if (questionnaireData.title1) {
          dynamicColumns.push({
            field: "answer1",
            headerName: "質問１の回答",
            style: "w-1/6",
            sort: false,
          });
        }
        if (questionnaireData.title2) {
          dynamicColumns.push({
            field: "answer2",
            headerName: "質問２の回答",
            style: "w-1/6",
            sort: false,
          });
        }
        if (questionnaireData.title3) {
          dynamicColumns.push({
            field: "answer3",
            headerName: "質問３の回答",
            style: "w-1/6",
            sort: false,
          });
        }
        setColumns(dynamicColumns);
        setCompany(company);
        setIsLoad(false);
      } catch (error) {
        dispatch(alertOpen(AlertValue("アンケート結果の取得に失敗しました。", "danger")));
        setIsLoad(false);
      }
    };
    f();
  }, [messageId, dispatch]);

  const onClickSearch = () => {
    const filteredRows = originalRowList.filter(row => {
      return (
        (filterName === "" || row.rowData.userName.includes(filterName)) &&
        (filterReplyList === "" || !isReplyListFilterActive || row.rowData.attendanceButtonName === filterReplyList) && 
        (filterAnswerResult === "" || row.rowData.answerResult === filterAnswerResult) &&
        (filterAnswer1 === "" || row.rowData.answer1 === filterAnswer1) &&
        (filterAnswer2 === "" || row.rowData.answer2 === filterAnswer2) &&
        (filterAnswer3 === "" || row.rowData.answer3 === filterAnswer3)
      );
    });
    setRowList(filteredRows);
  };

  const handleClick = () => {
    setSearchFlg(!searchFlg);
  };

  const aggHandleClick = () => {
    setAggregationFlg(!aggregationFlg);
  };

  const eventAt = message?.eventAt ? new Date(message.eventAt) : null;
  const eventEndAt = message?.eventEndAt ? new Date(message.eventEndAt) : null;

  return (
    <div className="h-full">
      <nav className="flex items-start px-4 py-3" aria-label="Breadcrumb">
        <div
          className="inline-flex items-center space-x-3 text-sm font-medium text-gray-900 cursor-pointer"
          onClick={() => navigate(`/message/${messageId}/info`)}
        >
          <ChevronLeftIcon
            className="-ml-2 h-5 w-5 text-gray-400"
            aria-hidden="true"
          />
          <span>詳細へ</span>
        </div>
      </nav>
      {isLoad ? (
        <Spiner />
      ) : (
        <>
          <div className="px-3 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-xl font-bold text-gray-900">アンケート結果</h2>
              <div className="flex space-x-2">
                <Button size="sm" onClick={() => navigate(`/message/${messageId}/info?scrollTo=deliveryResult`)}>
                  配信先一覧
                </Button>
                <Button size="sm" onClick={() => navigate(`/message/${messageId}/reply`)}>
                  返信一覧
                </Button>
                <Button
                  size="sm"
                  className={!message?.eventFlg ? "opacity-50 cursor-not-allowed" : ""}
                  onClick={() => navigate(`/message/${messageId}/attendance`)}
                >
                  出欠一覧
                </Button>
                <Button
                  size="sm"
                  disabled={!(message?.eventFlg && message?.municipalityConfirmedFlg)}
                  className={!(message?.eventFlg && message?.municipalityConfirmedFlg) ? "opacity-50 cursor-not-allowed" : ""}
                  onClick={() => navigate(`/eventParticipation/${messageId}`)}
                >
                  イベント参加履歴一覧
                </Button>
              </div>
            </div>
            <div className="mt-7"></div>
            <div className="flex items-center space-x-4">
              <h2 className="text-xl font-bold text-gray-900">
                {message?.title}
              </h2>
            </div>
            <div className="mt-1">
              {eventAt && eventEndAt ? (
                <p>
                  　イベント開催日時：{convertDateToJapaneseString(eventAt)} {convertTimeToString(eventAt)}～{convertTimeToString(eventEndAt)}
                </p>
                ) : (
                  <p></p>
                )}
            </div>
            <div className="mt-3" />
            <div className="flex items-center justify-between space-x-4">
              {questionnaire.target && (
                <p>
                  　回答対象：
                  {message?.replyButton === '予定表の出欠' && questionnaire.target === '全員' && (
                    <span>会場参加者</span>
                  )}
                  {message?.replyButton === '予定表の出欠(オンライン)' && questionnaire.target === '全員' && (
                    <span>会場・オンライン参加者</span>
                  )}
                  {questionnaire.target === 'オンライン参加のみ' && (
                    <span>オンライン参加者</span>
                  )}
                </p>
              )}
              <Button
                size={"sm"}
                className={"w-[132px]"}
                onClick={() => {setIsDialogOpen(true)}}
                children={"結果出力"}
              />
              <Progress
                subText={ConstMessage.PROGRESS_SUB_TEXT}
                open={loadProgress >= 0 && loadProgress < 100}
                progressNum={loadProgress}
                color={"indigo"}
              />
              {/* 結果出力確認モーダル */}
              <QuestionnaireResultsExportModal
                children={undefined}
                open={isDialogOpen}
                toggle={() => {setIsDialogOpen(!isDialogOpen)}}
                closeOnClickOutside={false}
                gridRow={rowList}
                company={company}
                message={message!}
                questionnaire={questionnaire}
                stateSetLoadProgress={setLoadProgress}
              />
            </div>
            <div className="mt-3"/>
            <div className="flex flex-col">
              <div className="inline-block min-w-full p-1 align-middle">
                <div className="relative overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
                  <div className="sm:items-center p-2">
                    <div className="flex justify-between cursor-pointer" onClick={aggHandleClick}>
                      <h2 className="flex font-semibold text-gray-900 items-center">
                        <ListBulletIcon className="flex-shrink-0 h-5 w-5" aria-hidden="true" />
                        回答状況
                      </h2>
                      {aggregationFlg ? (
                        <ChevronUpIcon className="flex-shrink-0 h-5 w-5" aria-hidden="true" />
                      ) : (
                        <ChevronDownIcon className="flex-shrink-0 h-5 w-5" aria-hidden="true" />
                      )}
                    </div>
                    {aggregationFlg && (
                      <>
                        <div className="flex flex-col gap-2 px-2 mt-2">
                          対象者合計：{answerCounts.total}名　　回答済：{answerCounts.answerResult["あり"]}名　未回答：{answerCounts.answerResult["なし"]}名
                          <div className="grid grid-cols-1 gap-2">
                            {questionnaire.title1 && (
                              <div className="mb-0.5 flex">
                                <p className="w-1/2">【質問１】{questionnaire.title1}</p>
                                <div className="w-1/2 ml-5">
                                  {Object.entries(answerCounts.answer1).map(([key, value]) => (
                                    <span key={key} className="mr-3">{key}：{value}名</span>
                                  ))}
                                </div>
                              </div>
                            )}
                            {questionnaire.title2 && (
                              <div className="mb-0.5 flex">
                                <p className="w-1/2">【質問２】{questionnaire.title2}</p>
                                <div className="w-1/2 ml-5">
                                  {Object.entries(answerCounts.answer2).map(([key, value]) => (
                                    <span key={key} className="mr-3">{key}：{value}名</span>
                                  ))}
                                </div>
                              </div>
                            )}
                            {questionnaire.title3 && (
                              <div className="mb-0.5 flex">
                                <p className="w-1/2">【質問３】{questionnaire.title3}</p>
                                <div className="w-1/2 ml-5">
                                  {Object.entries(answerCounts.answer3).map(([key, value]) => (
                                    <span key={key} className="mr-3">{key}：{value}名</span>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
            <div className="mt-1"/>
            <div className="flex flex-col">
              <div className="inline-block min-w-full p-1 align-middle">
                <div className="relative overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
                  <div className="sm:items-center p-2">
                    <div className="flex justify-between cursor-pointer" onClick={handleClick}>
                      <h2 className="flex font-semibold text-gray-900 items-center">
                        <MagnifyingGlassIcon
                          className="flex-shrink-0 h-5 w-5"
                          aria-hidden="true"
                        />
                        検索
                      </h2>
                      {searchFlg ? (
                        <ChevronUpIcon
                          className="flex-shrink-0 h-5 w-5"
                          aria-hidden="true"
                        />
                      ) : (
                        <ChevronDownIcon
                          className="flex-shrink-0 h-5 w-5"
                          aria-hidden="true"
                        />
                      )}
                    </div>
                    {searchFlg && (
                      <>
                        <div className="flex gap-6 px-2">
                          <div className="w-4/12 flex flex-col gap-1">
                            <div className="flex sm:items-center my-1 flex-wrap">
                              <div className="w-3/12">
                                <h3 className="text-sm text-gray-900">名前</h3>
                              </div>
                              <div className="ml-3">
                                <TextField
                                  className={'h-10 w-52'}
                                  inputClassName={`h-8`}
                                  value={filterName}
                                  onChange={(e: { target: { value: SetStateAction<string>; }; }) => setFilterName(e.target.value)}
                                />
                              </div>
                            </div>
                            <div className="flex flex-col gap-1">
                              <div className="flex items-center flex-wrap">
                                <div className="w-3/12">
                                  <h3 className="text-sm text-gray-900">質問１の回答</h3>
                                </div>
                                <SelectFields
                                  id="answer1"
                                  color="indigo"
                                  value={filterAnswer1}
                                  className={"h-10 w-52 ml-3"}
                                  selectItems={answers1List}
                                  onChange={(e: { target: { value: SetStateAction<string>; }; }) => setFilterAnswer1(e.target.value)}
                                />
                              </div>
                            </div>
                          </div>
                          <div className="w-4/12 flex flex-col gap-1">
                            <div className="flex items-center ml-1 flex-wrap mt-1">
                              <div className="w-3/12">
                                <h3 className="text-sm text-gray-900">出欠結果</h3>
                              </div>
                              <div className="ml-2">
                              <SelectFields
                                id="attendance"
                                color="indigo"
                                value={filterReplyList}
                                className={'h-10 w-52'}
                                selectItems={replyList}
                                onChange={(e: { target: { value: SetStateAction<string>; }; }) => setFilterReplyList(e.target.value)}
                                disabled={message?.replyButton === '予定表の出欠(オンライン)' && questionnaire.target === 'オンライン参加のみ'
                                      || message?.replyButton === '予定表の出欠' && questionnaire.target === '全員'}
                                style={{ backgroundColor: message?.replyButton === '予定表の出欠(オンライン)' && questionnaire.target === 'オンライン参加のみ'
                                      || message?.replyButton === '予定表の出欠' && questionnaire.target === '全員' ? "#d1d5db" : "white" }}
                              />
                              </div>
                            </div>
                            <div className="flex sm:items-center mt-1 flex-wrap">
                              <div className="w-3/12">
                                <h3 className="text-sm text-gray-900">質問２の回答</h3>
                              </div>
                              <SelectFields
                                id="answer2"
                                color="indigo"
                                value={filterAnswer2}
                                className={"h-10 w-52 ml-3"}
                                selectItems={answers2List}
                                onChange={(e: { target: { value: SetStateAction<string>; }; }) => setFilterAnswer2(e.target.value)}
                                disabled={!questionnaire.title2}
                                style={{ backgroundColor: !questionnaire.title2 ? "#d1d5db" : "white" }}
                              />
                            </div>
                          </div>
                          <div className="w-4/12 flex flex-col gap-1">
                            <div className="flex sm:items-center mt-1 flex-wrap">
                              <div className="w-3/12">
                                <h3 className="text-sm text-gray-900">回答状況</h3>
                              </div>
                              <SelectFields
                                id="answerResult"
                                color="indigo"
                                value={filterAnswerResult}
                                className={"h-10 w-52 ml-3"}
                                selectItems={answerResultList}
                                onChange={(e: { target: { value: SetStateAction<string>; }; }) => setFilterAnswerResult(e.target.value)}
                              />
                            </div>
                            <div className="flex sm:items-center mt-1 flex-wrap">
                              <div className="w-3/12">
                                <h3 className="text-sm text-gray-900">質問３の回答</h3>
                              </div>
                              <SelectFields
                                id="answer3"
                                color="indigo"
                                value={filterAnswer3}
                                className={"h-10 w-52 ml-3"}
                                selectItems={answers3List}
                                onChange={(e: { target: { value: SetStateAction<string>; }; }) => setFilterAnswer3(e.target.value)}
                                disabled={!questionnaire.title3}
                                style={{ backgroundColor: !questionnaire.title3 ? "#d1d5db" : "white" }}
                              />
                            </div>
                          </div>
                        </div>
                        <div className="flex sm:items-center justify-end mt-2">
                          <Button
                            className="h-10 w-32 text-sm sm:items-center"
                            onClick={onClickSearch}
                          >
                            検索
                          </Button>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
            <div className="mt-3 mb-5">
              <MessageInfoTableList
                header={false}
                footer={false}
                gridCol={columns}
                gridRow={rowList}
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
};